import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Form, Button, Modal, Switch, Divider, Skeleton } from 'antd';
import { isEmpty } from 'lodash';
import {
  useBankAccount,
  useDeleteBankAccount,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import { PageProps } from '@/types/PageProps';
import BankAccountForm from '@/components/Registry/BankAccounts/BankAccountForm';
import { CreateBankAccountFormValues } from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import { DefaultRouteTabs, SaveButton } from '@/components/_common';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import useValidateRoutes from '@/hooks/useValidateRoutes';
import { useBankAccountFormContext } from '../../BankAccountFormProvider';
import ButtonBack from '@/components/_common/ButtonBack';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Helmet } from 'react-helmet';
import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

function BankAccountsGeneralPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<CreateBankAccountFormValues>();
  const { bankAccountId } = useRouteMatch<{ bankAccountId: string }>().params;

  const {
    active,
    setActive,
    handleSubmit,
    handleActive,
    isLoading,
    contextForm,
    disabled,
  } = useBankAccountFormContext();

  const { data: bankAccount, isLoading: isLoadingBankAccountDetail } =
    useBankAccount(+bankAccountId);

  const deleteMutation = useDeleteBankAccount();

  const routeValidate = useValidateRoutes({
    route: route.parent,
    validation: !bankAccount?.bank.integration || !bankAccount?.companyId,
    keys: ['configurationBankSlip'],
  });

  useEffect(() => {
    if (contextForm.bankAccountGeneralForm) {
      form.setFieldsValue(contextForm.bankAccountGeneralForm);
    } else if (bankAccount && !isEmpty(bankAccount)) {
      form.setFieldsValue({
        ...bankAccount,

        accountAddress: {
          ...bankAccount?.accountAddress,
          zip: applyMask(bankAccount?.accountAddress?.zip, cepMask),
        },
      });

      setActive(bankAccount.active);
    }
  }, [bankAccount]);

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'bankAccounts.remove' }),
      content: `${intl.formatMessage({
        id: 'bankAccounts.remove.modal.content',
      })}`,
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(+bankAccountId);

          history.push('/app/financial/accounts/bankAccounts');
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'bankAccounts.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  useUpdateBreadcrumbParam(':bankAccountId', bankAccount?.bank.name);

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={routeValidate}
      baseKey={String(bankAccountId)}
      basePath={`/app/financial/accounts/bankAccounts/${bankAccountId}`}
      extra={
        <SpaceForButton>
          <Switch
            unCheckedChildren={intl.formatMessage({
              id: 'commons.entity.inactive',
            })}
            checkedChildren={intl.formatMessage({
              id: 'commons.entity.active',
            })}
            loading={isLoadingBankAccountDetail || isLoading}
            checked={active}
            onChange={handleActive}
            disabled={!hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_EDIT])}
          />

          <Button
            block
            danger
            onClick={handleRemoveClick}
            loading={deleteMutation.isLoading}
            disabled={
              !hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_DELETE])
            }
          >
            <FormattedMessage id="commons.form.button.remove" />
          </Button>

          <SaveButton
            block
            type="primary"
            onClick={async () => {
              await form.validateFields();

              handleSubmit();
            }}
            loading={isLoading}
            disabled={!hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_EDIT])}
          >
            <FormattedMessage id="commons.form.button.save" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet
        title={`${
          bankAccount?.bank.name ? `${bankAccount?.bank.name} | ` : ''
        }${intl.formatMessage({
          id: route.name,
        })}`}
      />

      <Skeleton loading={isLoadingBankAccountDetail}>
        <BankAccountForm form={form} bankAccount={bankAccount} />

        <Divider />

        <SpaceForButton>
          <ButtonBack
            path="/app/financial/accounts/bankAccounts"
            disabled={disabled}
          />

          <Button
            block
            type="primary"
            onClick={() =>
              history.push('/app/financial/accounts/bankAccounts/create')
            }
            disabled={!hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_ADD])}
          >
            Nova Conta bancária
          </Button>
        </SpaceForButton>
      </Skeleton>
    </DefaultRouteTabs>
  );
}

export default BankAccountsGeneralPage;
